import { action, computed, makeAutoObservable, observable, runInAction } from 'mobx';
import moment from 'moment';
import { ACCOUNT_TYPE_CODE, ACCOUNT_TYPE_MAP, USER_KEY } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { CORPORATE_FILE_TYPE, CORPORATE_FILE_TYPE_MAPPER, FILE_TYPES, FILE_TYPE_MAPPER } from 'src/constants/file';
import {
	AccountChangeBasicInfoDTO,
	AccountChangePasswordDTO,
	AccountInfoDTO,
	AccountInfoResponse,
	IAccountChangeBasicInfoBody,
	IUpdatePasswordBody
} from 'src/dto/account.dto';
import { ResponseDTO } from 'src/dto/base.dto';
import { OtpVerificationResponse } from 'src/dto/otp.dto';
import { DEFAULT_DATE_FORMAT, formatDate } from 'src/helpers/date.utils';
import {
	IAccountOnboardingDetail,
	IBankAccount,
	IEmploymentDetail,
	IFileRecord,
	IFunderSupportingDocument,
	ISuportingDocument
} from 'src/interfaces/accounts/account';
import { ICompanyInfo, ICompanyLevelDocument, ICorporateLevelDocuments } from 'src/interfaces/accounts/company';
import { IHttpService } from 'src/services/http.service';
import { IApplicationStore } from '../root/application.store';
import { IOnboardingStore } from './onboarding.store';
import { convertNumberToStringForObject } from 'src/helpers/common.utils';

export interface IUser {
	accountId?: string;
	email?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	companyName?: string;
	accountType?: number;
	isActive?: boolean;
	hasDashboard?: boolean;
	accountRegistrationDate?: Date;
	eSignedDocument?: ISuportingDocument;
}
export interface IUserStore extends IUser {
	accountDetail?: IAccountOnboardingDetail;
	employmentDetail?: IEmploymentDetail;
	individualFunderSupportingDocuments?: ISuportingDocument;
	individualBorrowerSupportingDocuments?: ISuportingDocument;
	funderRequestSubmitDate?: Date;
	corporateFunderLevelDocuments?: ICompanyLevelDocument;
	corporateBorrowerLevelDocuments?: ICompanyLevelDocument;
	eSignedDocument?: ISuportingDocument;
	borrowerRequestSubmitDate?: Date;
	linkMenuPrev?: string;
	get getFullName(): string;
	get avatarName(): string;
	get accountTypeMap(): string | undefined;
	companyDetail?: ICompanyInfo;
	bankAccount?: IBankAccount;
	setUser(data: IUser): void;
	removeUser(): void;
	changePassword(dto: IUpdatePasswordBody): Promise<boolean>;
	updateBasicInformation(dto: IAccountChangeBasicInfoBody): Promise<boolean>;
	fetchUserInfo(): Promise<void>;
	updateDashboard(value: boolean): void;
	signPlatformUsage(accountId: string, type: string): Promise<boolean>;
	get isSignedDocsFunder(): boolean;
	get isSignedDocsBorrower(): boolean;
	get isSignedDocsCorporateBorrower(): boolean;
	get dateOfIncorporation(): string;
	get byPassInformationPage(): boolean;

	get getCorporateBorrowerLevelDocuments(): IFileRecord[];
	get getCorporateFunderLevelDocuments(): IFileRecord[];

	get getIndividualFunderSupportingDocuments(): IFileRecord[];
	get getIndividualBorrowerSupportingDocuments(): IFileRecord[];
	dispose(): void;
	setPersonId(personId: string): void;
}

export class UserStore implements IUserStore {
	accountId?: string;
	email?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	companyName?: string;
	accountType?: number;
	isActive?: boolean;
	hasDashboard?: boolean;
	accountDetail?: IAccountOnboardingDetail;
	companyDetail?: ICompanyInfo;
	employmentDetail?: IEmploymentDetail;
	individualFunderSupportingDocuments?: ISuportingDocument;
	individualBorrowerSupportingDocuments?: ISuportingDocument;
	corporateFunderLevelDocuments?: ICompanyLevelDocument;
	corporateBorrowerLevelDocuments?: ICompanyLevelDocument;
	accountRegistrationDate?: Date;
	funderRequestSubmitDate?: Date;
	borrowerRequestSubmitDate?: Date;
	bankAccount?: IBankAccount;
	eSignedDocument?: ISuportingDocument;

	constructor(
		private readonly http: IHttpService,
		private readonly onboardingStore: IOnboardingStore,
		private readonly applicationStore: IApplicationStore
	) {
		const userLocal: string | null = localStorage.getItem(USER_KEY);
		if (userLocal) {
			this.setUser(JSON.parse(userLocal));
		}
		makeAutoObservable(this, {
			accountDetail: observable,
			employmentDetail: observable,
			getFullName: computed,
			hasDashboard: observable,
			companyDetail: observable,
			setUser: action.bound,
			removeUser: action.bound,
			avatarName: computed,
			accountTypeMap: computed,
			updateDashboard: action.bound,
			dateOfIncorporation: computed,
			byPassInformationPage: computed,
			getCorporateBorrowerLevelDocuments: computed,
			getCorporateFunderLevelDocuments: computed,
			dispose: action,
			setPersonId: action.bound
		});
	}

	get getIndividualBorrowerSupportingDocuments(): IFileRecord[] {
		if (!this.individualBorrowerSupportingDocuments) {
			return [];
		}

		const rs: IFileRecord[] = [];
		const keys = Object.keys(this.individualBorrowerSupportingDocuments);
		for (const k of keys) {
			const documents = this.individualBorrowerSupportingDocuments[k as keyof IFunderSupportingDocument] || [];
			for (const d of documents) {
				rs.push({
					key: d.id || '',
					id: d.id,
					type: FILE_TYPE_MAPPER.get(d.type),
					name: d.fileName,
					submittedBy: '',
					submittedDate: new Date().toISOString(),
					remoteType: FILE_TYPES[d.type]
				});
			}
		}
		return rs;
	}

	get getIndividualFunderSupportingDocuments(): IFileRecord[] {
		if (!this.individualFunderSupportingDocuments) {
			return [];
		}

		const rs: IFileRecord[] = [];
		const keys = Object.keys(this.individualFunderSupportingDocuments);
		for (const k of keys) {
			const documents = this.individualFunderSupportingDocuments[k as keyof IFunderSupportingDocument] || [];
			for (const d of documents) {
				rs.push({
					key: d.id || '',
					id: d.id,
					type: FILE_TYPE_MAPPER.get(d.type),
					name: d.fileName,
					submittedBy: '',
					submittedDate: new Date().toISOString(),
					remoteType: FILE_TYPES[d.type]
				});
			}
		}
		return rs;
	}

	get isSignedDocsFunder() {
		if (
			this.individualFunderSupportingDocuments?.declarationForm?.length &&
			this.individualFunderSupportingDocuments?.personalInformationCollectionStatement?.length &&
			this.individualFunderSupportingDocuments?.professionalInvestorNotice?.length
		) {
			return true;
		}
		return false;
	}

	get isSignedDocsBorrower() {
		if (
			this.individualBorrowerSupportingDocuments?.declarationForm?.length &&
			this.individualBorrowerSupportingDocuments?.personalInformationCollectionStatement?.length
		) {
			return true;
		}
		return false;
	}

	get isSignedDocsCorporateBorrower() {
		if (
			this.corporateBorrowerLevelDocuments?.declarationForm?.length &&
			this.corporateBorrowerLevelDocuments?.personalInformationCollectionStatement?.length
		) {
			return true;
		}
		return false;
	}

	get getCorporateBorrowerLevelDocuments(): IFileRecord[] {
		if (!this.corporateBorrowerLevelDocuments) {
			return [];
		}

		const rs: IFileRecord[] = [];
		const keys = Object.keys(this.corporateBorrowerLevelDocuments);
		for (const k of keys) {
			const documents = this.corporateBorrowerLevelDocuments[k as keyof ICorporateLevelDocuments] || [];
			for (const d of documents) {
				rs.push({
					key: d.id || '',
					id: d.id,
					type: CORPORATE_FILE_TYPE_MAPPER.get(d.companyType),
					name: d.fileName,
					submittedBy: '',
					submittedDate: new Date().toISOString(),
					remoteType: CORPORATE_FILE_TYPE[d.companyType]
				});
			}
		}
		return rs;
	}

	get getCorporateFunderLevelDocuments(): IFileRecord[] {
		if (!this.corporateFunderLevelDocuments) {
			return [];
		}

		const rs: IFileRecord[] = [];
		const keys = Object.keys(this.corporateFunderLevelDocuments);
		for (const k of keys) {
			const documents = this.corporateFunderLevelDocuments[k as keyof ICorporateLevelDocuments] || [];
			for (const d of documents) {
				rs.push({
					key: d.id || '',
					id: d.id,
					type: CORPORATE_FILE_TYPE_MAPPER.get(d.companyType),
					name: d.fileName,
					submittedBy: '',
					submittedDate: new Date().toISOString(),
					remoteType: CORPORATE_FILE_TYPE[d.companyType]
				});
			}
		}
		return rs;
	}

	get getFullName(): string {
		let fullName = `${this.firstName || ''} ${this.middleName || ''} ${this.lastName || ''}`;
		if (!this.firstName || !this.lastName) {
			fullName = this.companyName as string;
		}
		return fullName;
	}

	get avatarName(): string {
		if (this.accountType == ACCOUNT_TYPE_CODE.CORPORATE) {
			return this.companyName?.charAt(0) || '';
		}
		return `${this.firstName?.charAt(0)}${this.lastName?.charAt(0)}`.toUpperCase();
	}

	get accountTypeMap(): string | undefined {
		if (!this.accountType) {
			return '';
		}
		return ACCOUNT_TYPE_MAP.get(this.accountType);
	}

	public setPersonId(personId: string): void {
		this.accountDetail = {...this.accountDetail, personId}
	}
	public setUser(data: IUser) {
		this.accountId = data.accountId;
		this.email = data.email;
		this.firstName = data.firstName;
		this.middleName = data.middleName;
		this.lastName = data.lastName;
		this.accountId = data.accountId;
		this.accountType = data.accountType;
		this.companyName = data.companyName;
		this.accountRegistrationDate = data.accountRegistrationDate;
		this.eSignedDocument = data.eSignedDocument
		localStorage.setItem(USER_KEY, JSON.stringify(data));
	}

	public removeUser() {
		this.email = undefined;
		localStorage.removeItem(USER_KEY);
	}

	public async changePassword(dto: IUpdatePasswordBody): Promise<boolean> {
		const changePasswordDTO = new AccountChangePasswordDTO(dto);
		const res: ResponseDTO<OtpVerificationResponse> = await this.http.request<
			AccountChangePasswordDTO,
			OtpVerificationResponse
		>(changePasswordDTO);
		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			const data = res.data;
			runInAction(() => {
				this.http.setToken(data?.token);
				this.setUser({
					...data
				} as IUser);
			});
			return true;
		}
		return false;
	}

	public async updateBasicInformation(dto: IAccountChangeBasicInfoBody): Promise<boolean> {
		const form = new AccountChangeBasicInfoDTO(dto);
		const res: ResponseDTO<String> = await this.http.request<AccountChangeBasicInfoDTO, String>(form);
		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(async () => {
				await this.fetchUserInfo();
			});
			return true;
		}
		return false;
	}

	public async fetchUserInfo(): Promise<void> {
		const accountInfoDto = new AccountInfoDTO();
		const res: ResponseDTO<AccountInfoResponse> = await this.http.request<AccountInfoDTO, AccountInfoResponse>(
			accountInfoDto
		);
		if (res.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			const data = res.data;
			runInAction(() => {
				this.setUser({
					...data
				} as IUser);
				this.accountDetail = {
					...data?.accountOnboardingDetailResponse
				};
				if (this.accountDetail && this.accountDetail.dob) {
					this.accountDetail.dob = formatDate(this.accountDetail.dob);
				}

				if (this.accountDetail && this.accountDetail.chineseIdEffectiveDate) {
					this.accountDetail.chineseIdEffectiveDate = formatDate(this.accountDetail.chineseIdEffectiveDate);
				}

				if (this.accountDetail && this.accountDetail.chineseIdExpiryDate) {
					this.accountDetail.chineseIdExpiryDate = formatDate(this.accountDetail.chineseIdExpiryDate);
				}

				if (
					this.accountDetail &&
					!!this.accountDetail.hkId &&
					!!this.accountDetail.hkIdPart1 &&
					!!this.accountDetail.hkIdPart2
				) {
					this.accountDetail.hkIdNo = `${this.accountDetail.hkIdPart1} ${this.accountDetail.hkId} (${this.accountDetail.hkIdPart2})`;
				}

				this.onboardingStore.setFunderOnboardingRequest(data?.funderOnboardingRequest);
				this.onboardingStore.setBorrowerOnboardingRequest(data?.borrowerOnboardingRequest);
				if (data?.funderOnboardingRequest || data?.borrowerOnboardingRequest || data?.accountOnboardingDetailResponse) {
					this.updateDashboard(true);
				}
				this.companyDetail = data?.companyInfo;
				if (data && data.employmentDetail) {
					this.employmentDetail = convertNumberToStringForObject<IEmploymentDetail>(data.employmentDetail, [
						'annualSalary'
					]);
				}

				this.bankAccount = data?.bankAccount;

				this.individualFunderSupportingDocuments = data?.individualFunderSupportingDocuments;
				this.individualBorrowerSupportingDocuments = data?.individualBorrowerSupportingDocuments;
				this.corporateFunderLevelDocuments = data?.corporateFunderLevelDocuments;
				this.corporateBorrowerLevelDocuments = data?.corporateBorrowerLevelDocuments;
				this.funderRequestSubmitDate = data?.funderRequestSubmitDate;
				this.borrowerRequestSubmitDate = data?.borrowerRequestSubmitDate;
			});
		}
	}

	public updateDashboard(value: boolean): void {
		this.hasDashboard = value;
	}

	public async signPlatformUsage(accountId: string, type: string): Promise<boolean> {
		const formData = new FormData();

		formData.append('accountId', accountId as string);
		formData.append('type', type[0].toUpperCase() + type.slice(1));

		const rs = await this.http.uploadFile<string>(ENDPOINT.UPLOAD_PLATFORM_USAGE, formData);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}
		return false;
	}

	get dateOfIncorporation(): string {
		if (!this.companyDetail || !this.companyDetail.dateOfIncorporation) {
			return '';
		}
		return moment(this.companyDetail.dateOfIncorporation).format(DEFAULT_DATE_FORMAT);
	}

	get byPassInformationPage(): boolean {
		return (!!this.accountDetail && !!this.accountDetail.personId) || (!!this.companyDetail && !!this.companyDetail.id);
	}

	public dispose(): void {
		this.employmentDetail = undefined;
		this.accountDetail = undefined;
		this.companyDetail = undefined;
		this.setUser({});
		this.hasDashboard = false;
		this.employmentDetail = undefined;
	}
}
